import { create } from "zustand";
import axios from "axios";
import { getToken } from "@/utils/localstorage";

export type SlideFrameId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface SlidesDialogTimePeriod {
  startDate: string;
  endDate: string;
}

export interface SlidesDialogTwoChartTimePeriod {
  chart1: SlidesDialogTimePeriod;
  chart2: SlidesDialogTimePeriod;
}

export type SlidesDialogBenchmark =
  | { type: "company"; ticker: string; exchange: string; name: string }
  | { type: "index"; ticker: string; exchange: string; name: string }
  | { type: "custom_built_index" };

export interface SlidesDialogCustomBuiltIndex {
  indexName: string;
  constituentContributionApproach: "equal-weighted";
  constituents: Array<{ ticker: string; exchange: string; name: string }>;
}

export interface SlidesDialogFrameVersion {
  version: number;
  timePeriod: SlidesDialogTimePeriod;
  twoChartTimePeriod?: SlidesDialogTwoChartTimePeriod;
  benchmarks: SlidesDialogBenchmark[];
  customBuiltIndices: SlidesDialogCustomBuiltIndex[];
}

export interface SlidesDialogPerFrameSelection {
  slideFrameId: SlideFrameId;
  versions: SlidesDialogFrameVersion[];
}

export interface SlidesDialogOutput {
  perFrameSelections: SlidesDialogPerFrameSelection[];
}

interface SlidesDialogState {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  slidesDialogOutput: SlidesDialogOutput | null;
  selectedSlideFrameIds: SlideFrameId[] | null;

  initialize: (
    primaryTicker: string,
    primaryExchange: string,
    primaryCompanyName: string,
    selectedSlideFrameIds: SlideFrameId[],
    userFirstName?: string
  ) => Promise<string>;

  interact: (message: string) => Promise<string>;
  complete: () => Promise<{
    slidesDialogOutput: SlidesDialogOutput;
    selectedSlideFrameIds: SlideFrameId[];
  }>;
  reset: () => Promise<void>;
}

async function authHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function apiBase() {
  const apiUrl = process.env.API_URL;
  if (!apiUrl) {
    throw new Error("API_URL is not set.");
  }
  return apiUrl.replace(/\/$/, "");
}

export const useSlidesDialogStore = create<SlidesDialogState>((set) => ({
  isInitialized: false,
  isLoading: false,
  error: null,
  slidesDialogOutput: null,
  selectedSlideFrameIds: null,

  initialize: async (
    primaryTicker,
    primaryExchange,
    primaryCompanyName,
    selectedSlideFrameIds,
    userFirstName
  ) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${apiBase()}/slide-generation/dialog/initialize`;
      const response = await axios.post<{ openingMessage: string }>(
        url,
        {
          source: "ppt",
          flow: "slides",
          primaryTicker,
          primaryExchange,
          primaryCompanyName,
          selectedSlideFrameIds,
          userFirstName: userFirstName ?? "",
        },
        { headers: await authHeaders(), withCredentials: true }
      );
      set({
        isInitialized: true,
        isLoading: false,
        selectedSlideFrameIds: [...selectedSlideFrameIds],
      });
      return response.data.openingMessage;
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to initialize slides dialog";
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  interact: async (message) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${apiBase()}/slide-generation/dialog/interact`;
      const response = await axios.post<{ response: string }>(
        url,
        { source: "ppt", flow: "slides", message },
        { headers: await authHeaders(), withCredentials: true }
      );
      set({ isLoading: false });
      return response.data.response;
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to send message";
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  complete: async () => {
    set({ isLoading: true, error: null });
    try {
      const url = `${apiBase()}/slide-generation/dialog/complete`;
      const response = await axios.post<{
        slidesDialogOutput: SlidesDialogOutput;
        selectedSlideFrameIds: SlideFrameId[];
      }>(
        url,
        { source: "ppt", flow: "slides" },
        { headers: await authHeaders(), withCredentials: true }
      );
      const { slidesDialogOutput, selectedSlideFrameIds } = response.data;
      set({
        isLoading: false,
        slidesDialogOutput,
        selectedSlideFrameIds,
        isInitialized: false,
      });
      return { slidesDialogOutput, selectedSlideFrameIds };
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to complete slides dialog";
      set({ isLoading: false, error: msg });
      throw new Error(msg);
    }
  },

  reset: async () => {
    try {
      const url = `${apiBase()}/slide-generation/dialog/reset`;
      await axios.post(
        url,
        { source: "ppt", flow: "slides" },
        { headers: await authHeaders(), withCredentials: true }
      );
    } catch {
      // best-effort
    }
    set({
      isInitialized: false,
      isLoading: false,
      error: null,
      slidesDialogOutput: null,
      selectedSlideFrameIds: null,
    });
  },
}));
